<template>
  <div class="organization-roaming-config pa-10" v-if="organization">

    <div v-if="editing" class="d-flex justify-end">
      <audit-trail-button entity-type="Organization" :entity-id="organization.id" :fields="historyFields" />
    </div>


    <!-- READ MODE -->
    <template v-if="!editing">

      <v-row class="mb-2">
        <v-col><headline class="mb-0">{{ $t('organization.roaming-settings') }}</headline></v-col>
        <v-col class="text-right">
          <v-btn small @click="startEdit">
            <v-icon left>mdi-cog</v-icon>
            {{ $t('edit') }}
          </v-btn>
          <audit-trail-button class="ml-2" entity-type="Organization" :entity-id="organization.id" :fields="historyFields" />
        </v-col>
      </v-row>
      <div class="grid">
        <v-row>
          <v-col class="label" sm="3">{{ $t('organization.gireve-emsp-roaming') }} :</v-col>
          <v-col sm="1"><v-switch disabled v-model="organization.gireveOcpiEmp" /></v-col>
          <v-col class="label" sm="2">{{ $t('organization.gireve-cpo-roaming') }} :</v-col>
          <v-col sm="1"><v-switch disabled v-model="organization.gireveOcpiCpo" /></v-col>
          <v-col class="label" sm="2">{{ $t('organization.hubject-roaming') }} :</v-col>
          <v-col sm="1"><v-switch disabled v-model="organization.hubject" /></v-col>
        </v-row>
      </div>

      <headline class="mt-10">{{ $t('organization.operator-parameters-cpo') }}</headline>
      <div class="grid">
        <v-row>
          <v-col class="label" sm="3">{{ $t('organization.roaming-identifier') }} :</v-col>
          <v-col sm="3">{{ organization.cpoOperator && organization.cpoOperator.name ? `${organization.cpoOperator.name} (${organization.cpoOperator.id})` : '-' }}</v-col>
          <v-col class="label" sm="3">{{ $t('organization.cpo-private') }} :</v-col>
          <v-col sm="3"><v-switch disabled v-model="organization.cpoPrivate" /></v-col>
        </v-row>
        <v-row>
          <v-col class="label" sm="3">{{ $t('organization.heartbeat-frequency') }} :</v-col>
          <v-col sm="3">{{ organization.cpoHeartbeatFrequency != null ? organization.cpoHeartbeatFrequency + 's' : '-' }}</v-col>
          <v-col class="label" sm="3">{{ $t('organization.missed-heartbeats') }} :</v-col>
          <v-col sm="3">{{ organization.cpoMissedHeartbeats != null ? organization.cpoMissedHeartbeats : '-' }}</v-col>
        </v-row>
        <v-row>
          <v-col class="label" sm="3">{{ $t('organization.emp-fee-for-service') }} :</v-col>
          <v-col sm="3">{{ organization.cpoOneServiceEmpOrganizationName || '-' }}</v-col>
          <v-col class="label" sm="3">{{ $t('organization.whitelist-rfids') }} :</v-col>
          <v-col sm="3"><v-switch disabled v-model="organization.cpoRfidOnWhiteList" /></v-col>
        </v-row>
        <v-row v-if="organization.cpoPrivate">
          <v-col class="text-center">
            <v-btn small @click="openCpoPrivateAccess">
              {{ $t('organization.cpo-private-access.manage') }}
            </v-btn>
          </v-col>
        </v-row>
      </div>

      <headline class="mt-10">{{ $t('organization.mobility-provider-information-emp') }}</headline>
      <div class="grid">
        <v-row>
          <v-col class="label" sm="3">{{ $t('organization.roaming-identifier') }} :</v-col>
          <v-col sm="9">{{ organization.empOperator && organization.empOperator.name ? `${organization.empOperator.name} (${organization.empOperator.id})` : '-' }}</v-col>
        </v-row>
        <v-row>
          <v-col class="label" sm="3">{{ $t('organization.minimum-monthly-fee') }} :</v-col>
          <v-col sm="3">{{ organization.empMinimumMonthlyFee != null ? organization.empMinimumMonthlyFee + ' €' : '-' }}</v-col>
          <v-col class="label" sm="3">{{ $t('organization.authorize-rfid-ordering') }} :</v-col>
          <v-col sm="3"><v-switch disabled v-model="organization.empAllowRfidOrdering" /></v-col>
        </v-row>
        <v-row>
          <v-col class="label" sm="3">{{ $t('organization.rfid-tags-price') }} :</v-col>
          <v-col sm="3">{{ organization.empRfidTagPrice != null ? organization.empRfidTagPrice + ' €' : '-' }}</v-col>
          <v-col class="label" sm="3">{{ $t('organization.price-of-rfid-at-registration') }} :</v-col>
          <v-col sm="3">{{ organization.empRfidTagRegisterPrice != null ? organization.empRfidTagRegisterPrice + ' €' : '-' }}</v-col>
        </v-row>
        <v-row>
          <v-col class="label" sm="3">{{ $t('organization.customer-support-phone') }} :</v-col>
          <v-col sm="3">{{ organization.empSupportPhoneNumber || '-' }}</v-col>
          <v-col class="label" sm="3">{{ $t('organization.customer-support-email-address') }} :</v-col>
          <v-col sm="3">{{ organization.empSupportEmail || '-' }}</v-col>
        </v-row>
      </div>

    </template>

    <!-- EDIT MODE -->
    <template v-if="editing">
      <v-form ref="form" @submit.prevent="save">

        <headline>{{ $t('organization.roaming-settings') }}</headline>
        <v-row>
          <v-col cols="12" md="6">
            <form-field :label="$t('organization.gireve-emsp-roaming')" type="switch" v-model="form.gireveOcpiEmp" />
            <form-field :label="$t('organization.gireve-cpo-roaming')" type="switch" v-model="form.gireveOcpiCpo" />
          </v-col>
          <v-col cols="12" md="6">
            <form-field :label="$t('organization.hubject-roaming')" type="switch" v-model="form.hubject" />
          </v-col>
        </v-row>

        <headline>{{ $t('organization.operator-parameters-cpo') }}</headline>
        <v-row>
          <v-col cols="12" md="6">
            <form-field
                :label="$t('organization.roaming-identifier')"
                v-model="form.cpoOperatorId"
                type="select"
                :items="cpoOperators"
                item-value="id"
                clearable
            >
              <template #item="{item}">{{ item.name }} ({{ item.id }})</template>
              <template #selection="{item}">{{ item.name }} ({{ item.id }})</template>
            </form-field>
            <form-field :label="`${$t('organization.heartbeat-frequency')} (${$t('seconds')})`" v-model.number="form.cpoHeartbeatFrequency" input-type="number" clearable />
            <form-field :label="$t('organization.missed-heartbeats')" v-model.number="form.cpoMissedHeartbeats" input-type="number" clearable />
            <form-field :label="$t('organization.cpo-private')" type="switch" v-model="form.cpoPrivate" />
          </v-col>
          <v-col cols="12" md="6">
            <form-field
                :label="$t('organization.emp-fee-for-service')"
                type="tree-select-organization"
                v-model="form.cpoOneServiceEmpOrganizationId"
                clearable
            />
            <form-field :label="$t('organization.whitelist-rfids')" type="switch" v-model="form.cpoRfidOnWhiteList" />
          </v-col>
        </v-row>
        <v-row v-if="form.cpoPrivate">
          <v-col class="text-center">
            <v-btn small @click="openCpoPrivateAccess">
              {{ $t('organization.cpo-private-access.manage') }}
            </v-btn>
          </v-col>
        </v-row>

        <headline>{{ $t('organization.mobility-provider-information-emp') }}</headline>
        <v-row>
          <v-col cols="12" md="6">
            <form-field
                :label="$t('organization.roaming-identifier')"
                v-model="form.empOperatorId"
                type="select"
                :items="empOperators"
                item-value="id"
                clearable
            >
              <template #item="{item}">{{ item.name }} ({{ item.id }})</template>
              <template #selection="{item}">{{ item.name }} ({{ item.id }})</template>
            </form-field>
            <form-field :label="$t('organization.minimum-monthly-fee')" v-model.number="form.empMinimumMonthlyFee" input-type="number" clearable />
            <form-field :label="$t('organization.authorize-rfid-ordering')" type="switch" v-model="form.empAllowRfidOrdering" />
            <form-field :label="$t('organization.rfid-tags-price')" v-model.number="form.empRfidTagPrice" input-type="number" clearable />
            <form-field :label="$t('organization.price-of-rfid-at-registration')" v-model.number="form.empRfidTagRegisterPrice" input-type="number" clearable />
          </v-col>
          <v-col cols="12" md="6">
            <form-field :label="$t('organization.minimum-amount-for-fee-for-service')" v-model.number="form.empMinimumAmountForFeeForService" input-type="number" clearable />
            <form-field :label="$t('organization.margin-for-fee-for-service')" v-model.number="form.empMarginForFeeForService" input-type="number" clearable>
              <template #append>%</template>
            </form-field>
            <form-field type="phone" :label="$t('organization.customer-support-phone')" v-model="form.empSupportPhoneNumber" clearable />
            <form-field :label="$t('organization.customer-support-email-address')" v-model="form.empSupportEmail" :rules="[commonRules.email]" input-type="email" clearable />
          </v-col>
        </v-row>

        <v-row class="mt-4">
          <v-col class="d-flex justify-center">
            <v-btn small class="mr-5" @click="editing = false">{{ $t('cancel') }}</v-btn>
            <v-btn small color="primary" type="submit" :loading="saving">{{ $t('save') }}</v-btn>
          </v-col>
        </v-row>
      </v-form>
    </template>

    <cpo-private-access-modal ref="cpoPrivateAccess" v-if="organization" :organization="organization" />

  </div>
</template>

<script>
import AuditTrailButton from "@elements/AuditTrailButton.vue";

/** Les champs de ce formulaire, pour son historique. */
const ROAMING_FIELDS = ['gireveOcpiEmp','gireveOcpiCpo','hubject','cpoOperatorId','cpoPrivate','cpoOneServiceEmpOrganizationId','cpoRfidOnWhiteList','empOperatorId','empAllowRfidOrdering','empSupportPhoneNumber','empSupportEmail']
import OrganizationRepository from "@repository/OrganizationRepository";
import OperatorRepository from "@repository/OperatorRepository";
import CpoPrivateAccessModal from "@modals/CpoPrivateAccessModal";
import RulesMixin from "@mixins/RulesMixin";

export default {
  components: {AuditTrailButton,  CpoPrivateAccessModal },
  mixins: [RulesMixin],

  data() {
    return {
      editing: false,
      saving: false,
      operators: [],
      form: {},
    }
  },

  computed: {

    historyFields() {

      return ROAMING_FIELDS

    },
    organization() {
      return this.$parent.organization ?? null
    },

    cpoOperators() {
      return this.operators.filter(o => o.isCpo)
    },

    empOperators() {
      return this.operators.filter(o => o.isEmp)
    },
  },

  methods: {
    openCpoPrivateAccess() {
      this.$refs.cpoPrivateAccess.open()
    },

    startEdit() {
      this.loadOperators()
      this.form = {
        gireveOcpiEmp: this.organization.gireveOcpiEmp,
        gireveOcpiCpo: this.organization.gireveOcpiCpo,
        hubject: this.organization.hubject,
        cpoOperatorId: this.organization.cpoOperator?.id || null,
        cpoHeartbeatFrequency: this.organization.cpoHeartbeatFrequency,
        cpoMissedHeartbeats: this.organization.cpoMissedHeartbeats,
        cpoPrivate: this.organization.cpoPrivate,
        cpoOneServiceEmpOrganizationId: this.organization.cpoOneServiceEmpOrganizationId,
        cpoRfidOnWhiteList: this.organization.cpoRfidOnWhiteList,
        empOperatorId: this.organization.empOperator?.id || null,
        empMinimumMonthlyFee: this.organization.empMinimumMonthlyFee,
        empAllowRfidOrdering: this.organization.empAllowRfidOrdering,
        empRfidTagPrice: this.organization.empRfidTagPrice,
        empRfidTagRegisterPrice: this.organization.empRfidTagRegisterPrice,
        empMinimumAmountForFeeForService: this.organization.empMinimumAmountForFeeForService,
        empMarginForFeeForService: this.organization.empMarginForFeeForService,
        empSupportPhoneNumber: this.organization.empSupportPhoneNumber,
        empSupportEmail: this.organization.empSupportEmail,
      }
      this.editing = true
    },

    loadOperators() {
      if (this.operators.length > 0) return
      OperatorRepository.search({ isInternal: true, limit: 1000 }).then(operators => {
        this.operators = operators
      }).catch(err => {
        this.notifyError(err)
      })
    },

    save() {
      if (!this.$refs.form.validate()) return
      this.saving = true
      OrganizationRepository.updateRoamingConfig(this.organization.id, this.form).then(() => {
        this.saving = false
        this.editing = false
        this.$parent.loadData()
      }).catch(e => {
        this.saving = false
        this.notifyError(e)
      })
    },
  },
}
</script>
